/* =============================================================================
   admin-emails.jsx  —  Emails tab: edit the transactional email templates and
   send a mass invitation to selected RSVP guests.
============================================================================= */
import { useState, useEffect } from 'react';
import { Field, Input, TextArea, Loading, ErrorBlock, useAsyncData, Note } from './ui';
import { DB } from '../lib/api';

/* client-side preview render (mirrors the server's {{key}} substitution) */
function renderPreview(tpl, vars) {
  return String(tpl || '').replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
}

const TEMPLATE_META = [
  { key: 'rsvpAccept', label: 'RSVP confirmation — accepting', to: 'Sent to a guest who joyfully accepts.' },
  { key: 'rsvpDecline', label: 'RSVP confirmation — declining', to: 'Sent to a guest who regretfully declines.' },
  { key: 'hostNotify', label: 'Host notification (to you)', to: 'Sent to you on every RSVP.' },
];

const VAR_HINT =
  'Variables: {{firstName}} {{lastName}} {{hosts}} {{dateDisplay}} {{time}} {{endTime}} {{venueName}} {{venueAddress}}. Host notification also: {{attending}} {{partySize}} {{partyNames}} {{note}}.';

function sampleVars(settings) {
  const e = settings?.event || {};
  return {
    firstName: 'Sam', lastName: 'Rivera', attending: 'accepting', partySize: 2,
    partyNames: 'Sam and baby June', note: 'So excited for you both!',
    hosts: e.hosts || 'Kenna and Anthony', dateDisplay: e.dateDisplay || 'Saturday, August 8, 2026',
    time: e.time || '11:00 AM', endTime: e.endTime || '2:00 PM',
    venueName: e.venueName || '', venueAddress: e.venueAddress || '',
  };
}

/* ----------------------------- templates editor -------------------------- */
function EmailTemplatesEditor({ settings }) {
  const { loading, error, data, reload } = useAsyncData(() => DB.getEmailTemplates(), []);
  const [tpls, setTpls] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  useEffect(() => { if (data) setTpls(data); }, [data]);

  if (loading) return <div className="admin-card"><Loading label="Loading templates" /></div>;
  if (error) return <div className="admin-card"><ErrorBlock error={error} onRetry={reload} /></div>;
  if (!tpls) return null;

  const vars = sampleVars(settings);
  const edit = (key, field) => (v) => { setTpls((t) => ({ ...t, [key]: { ...t[key], [field]: v } })); setStatus('idle'); };

  async function save() {
    setStatus('saving');
    try { await DB.saveEmailTemplates(tpls); setStatus('done'); setTimeout(() => setStatus('idle'), 2000); }
    catch (e) { console.error(e); setStatus('error'); }
  }

  return (
    <div className="admin-card">
      <h3>Email templates</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Edit the automatic emails. Write the body as HTML so it is not a wall of text. {VAR_HINT}
      </p>
      {TEMPLATE_META.map(({ key, label, to }) => (
        <div key={key} style={{ borderTop: '1px solid var(--line)', paddingTop: 16, marginTop: 16 }}>
          <h4 style={{ margin: '0 0 2px', color: 'var(--terra-deep)' }}>{label}</h4>
          <p className="faint" style={{ fontSize: '0.82rem', marginTop: 0 }}>{to}</p>
          <Field label="Subject"><Input value={tpls[key].subject} onChange={edit(key, 'subject')} /></Field>
          <Field label="Body (HTML)">
            <TextArea value={tpls[key].html} onChange={edit(key, 'html')} style={{ minHeight: 140, fontFamily: 'monospace', fontSize: '0.85rem' }} />
          </Field>
          <div className="faint" style={{ fontSize: '0.8rem', marginBottom: 6 }}>Preview (with sample guest):</div>
          <div className="email-preview" dangerouslySetInnerHTML={{ __html: renderPreview(tpls[key].html, vars) }} />
        </div>
      ))}
      <div className="row" style={{ marginTop: 18, alignItems: 'center' }}>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={status === 'saving'}>
          {status === 'saving' ? 'Saving...' : 'Save templates'}
        </button>
        {status === 'done' && <span className="pill">Saved</span>}
        {status === 'error' && <span style={{ color: 'var(--ladybug)', fontSize: '0.85rem' }}>Could not save. Try again.</span>}
      </div>
    </div>
  );
}

/* ------------------------------- broadcast ------------------------------- */
function BroadcastComposer({ settings }) {
  const { loading, error, data, reload } = useAsyncData(() => DB.getRSVPs(), []);
  const [selected, setSelected] = useState(() => new Set());
  const [subject, setSubject] = useState('Your invitation to our baby shower');
  const [html, setHtml] = useState('<p>Hi {{firstName}},</p>\n<p>Here is your invitation to celebrate with {{hosts}} on {{dateDisplay}}.</p>');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [result, setResult] = useState(null);

  if (loading) return <div className="admin-card"><Loading label="Loading guests" /></div>;
  if (error) return <div className="admin-card"><ErrorBlock error={error} onRetry={reload} /></div>;

  const guests = [...data].filter((r) => r.email).sort((a, b) => b.timestamp - a.timestamp);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = guests.length > 0 && guests.every((g) => selected.has(g.id));
  const selectAll = () => setSelected(allSelected ? new Set() : new Set(guests.map((g) => g.id)));
  const vars = sampleVars(settings);

  async function send() {
    if (selected.size === 0) return;
    if (!window.confirm(`Send this email to ${selected.size} guest${selected.size === 1 ? '' : 's'}? This sends real email.`)) return;
    setStatus('sending'); setResult(null);
    try {
      const res = await DB.broadcastEmail({ subject, html, recipientIds: [...selected] });
      setResult(res); setStatus('done');
    } catch (e) { console.error(e); setStatus('error'); }
  }

  return (
    <div className="admin-card">
      <h3>Send the invitation</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Compose a message and send it to the guests you pick. Use {'{{firstName}}'} to personalize. Only guests who left an email are listed.
      </p>

      <Field label="Subject"><Input value={subject} onChange={setSubject} /></Field>
      <Field label="Message (HTML)">
        <TextArea value={html} onChange={setHtml} style={{ minHeight: 160, fontFamily: 'monospace', fontSize: '0.85rem' }} />
      </Field>
      <div className="faint" style={{ fontSize: '0.8rem', marginBottom: 6 }}>Preview (with sample guest):</div>
      <div className="email-preview" dangerouslySetInnerHTML={{ __html: renderPreview(html, vars) }} />

      <div className="row no-print" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <h4 style={{ margin: 0 }}>Recipients ({selected.size} of {guests.length} selected)</h4>
        <button className="btn btn-ghost btn-sm" onClick={selectAll} disabled={!guests.length}>
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      {guests.length === 0 ? (
        <p className="faint">No guests with an email yet.</p>
      ) : (
        <div className="table-scroll" style={{ maxHeight: 280, overflowY: 'auto', marginTop: 8 }}>
          {guests.map((g) => (
            <label key={g.id} className="check" style={{ display: 'flex', gap: 10, padding: '7px 2px', borderBottom: '1px solid var(--line-soft)' }}>
              <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggle(g.id)} />
              <span style={{ flex: 1 }}>
                {g.firstName} {g.lastName}
                <span className="faint" style={{ marginLeft: 8, fontSize: '0.82rem' }}>{g.email}</span>
              </span>
              <span className="faint" style={{ fontSize: '0.8rem' }}>{g.attending === 'accept' ? 'Accepting' : 'Declining'}</span>
            </label>
          ))}
        </div>
      )}

      {status === 'done' && result && (
        <Note tone="sage" style={{ marginTop: 14 }}>
          Sent to {result.sent} guest{result.sent === 1 ? '' : 's'}.{result.failed ? ` ${result.failed} failed.` : ''}
        </Note>
      )}
      {status === 'error' && <Note tone="error" style={{ marginTop: 14 }}>Could not send just now. Please try again.</Note>}

      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={send} disabled={status === 'sending' || selected.size === 0}>
        {status === 'sending' ? 'Sending...' : `Send to ${selected.size} guest${selected.size === 1 ? '' : 's'}`}
      </button>
    </div>
  );
}

export function EmailsTab({ settings }) {
  return (
    <div>
      <EmailTemplatesEditor settings={settings} />
      <BroadcastComposer settings={settings} />
    </div>
  );
}
